'use client';

import LoginForm from '@/components/auth/LoginForm';
import LoginFormCompat from '@/components/auth/LoginFormCompat';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { FeatureFlag, useFeatureFlag } from '@/lib/config/features';
import { getSafeRedirectPath } from '@/lib/utils/safe-redirect';

// Import AuthDebug only on client side with no SSR to avoid hydration issues
const AuthDebug = dynamic(() => import('./AuthDebug'), { ssr: false });
const AuthDebugCompat = dynamic(() => import('./AuthDebugCompat'), { ssr: false });

// Client-only wrapper component to ensure AuthDebug is only rendered on client
function ClientOnlyAuthDebug() {
  const [isMounted, setIsMounted] = useState(false);
  // After migration, we always use the improved auth system
  const useServerAuth = useFeatureFlag(FeatureFlag.USE_SERVER_AUTH);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  if (!isMounted || process.env.NODE_ENV === 'production') return null;
  
  // Always use the compatibility AuthDebug component after migration
  return <AuthDebugCompat />;
}

// Map of error codes to user-friendly messages
const ERROR_MESSAGES: Record<string, string> = {
  'auth_error': 'Authentication failed. Please try again.',
  'profile_creation_failed': 'Failed to create user profile. Please contact support.',
  'missing_code': 'Invalid login link. Please request a new one.',
  'unexpected': 'An unexpected error occurred. Please try again.',
};

interface LoginContentProps {
  error?: string;
  message?: string;
  redirectForAuth?: boolean;
  isSignUp?: boolean;
}

// A component that safely uses searchParams inside a Suspense boundary
function LoginContent({ error, message, redirectForAuth, isSignUp }: LoginContentProps) {
  // Use Supabase auth context directly
  const { isAuthenticated, isLoading, status, isUsingImprovedAuth, session } = useSupabaseAuth();
  
  // Map status to authStatus for backward compatibility
  const authStatus = status;
  const router = useRouter();
  const searchParams = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [serverValidationAttempts, setServerValidationAttempts] = useState(0);
  const maxServerValidationAttempts = 3; // Maximum retries for server validation
  const urlSignUp = searchParams.get('signup') === 'true';
  // Use either the prop or the URL parameter
  const shouldShowSignUp = isSignUp || urlSignUp;
  
  // After migration, we always use the improved form
  const useImprovedForm = true;
  const showDebugInfo = process.env.NODE_ENV === 'development';
  
  // Set mounted state to true on client-side and add URL listener
  useEffect(() => {
    setMounted(true);
    
    // Set up a listener for URL changes
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const signupParam = params.get('signup');
      console.log('[LoginClient] URL changed, signup param:', signupParam);
    };
    
    // Add event listener for URL changes
    window.addEventListener('popstate', handleUrlChange);
    
    // Clear orphaned auth verifiers that cause crashes
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        const tokenKey = `sb-${projectRef}-auth-token`;
        const verifierKey = `${tokenKey}-code-verifier`;
        
        // If we have inconsistent state, clean it up
        if (!localStorage.getItem(tokenKey) && localStorage.getItem(verifierKey)) {
          console.log('[LoginClient] Cleaning up inconsistent auth state');
          localStorage.removeItem(verifierKey);
        }
      }
    } catch (e) {
      console.error('[LoginClient] Error cleaning up auth state:', e);
    }
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);
  
  // Create a clean reset function
  const resetAuthState = () => {
    if (typeof localStorage !== 'undefined') {
      const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
      localStorage.removeItem(`sb-${projectRef}-auth-token`);

      // Avoid endless loop by checking if we already have the auth_state_reset param.
      // Preserve the existing query params (signup/invite/next) so an invite
      // return trip survives the reset.
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has('auth_state_reset')) {
        currentUrl.searchParams.set('auth_state_reset', 'true');
        window.location.replace(`/login?${currentUrl.searchParams.toString()}`);
      } else {
        // Just stay on the login page without adding more parameters
        console.log('[LoginClient] Already on reset page, not redirecting again');
      }
    }
  };

  // Verify server-side auth status on load (but with debounce).
  // `attempt` is passed explicitly rather than read from state: the retry
  // setTimeout below would otherwise capture a stale closure copy of the
  // counter, freezing the backoff at its initial value and never reaching
  // the max-attempts cutoff.
  const verifySeverSession = async (attempt = 0) => {
    // NEVER redirect on an anonymous session: Pattern A signup mints one
    // mid-flow (signInAnonymously → updateUser({ email })), and a
    // location.replace from here aborts the in-flight updateUser, stranding
    // the signup as an orphaned anonymous user. Room guests who land on
    // /login should also stay so they can sign in properly.
    // Checked against the LIVE session, not the render closure — a retry
    // scheduled before Pattern A minted its anonymous session would pass a
    // closure-based check.
    try {
      const { createBrowserClient } = await import('@/lib/supabase-clients');
      const { data: { session: liveSession } } = await createBrowserClient().auth.getSession();
      if (liveSession?.user?.is_anonymous) {
        console.log('[LoginClient] Anonymous session detected, skipping server validation redirect');
        return;
      }
    } catch (e) {
      console.error('[LoginClient] Error reading live session, skipping redirect:', e);
      return;
    }

    // Only verify server auth if client reports authenticated
    if (isAuthenticated && !isLoading && authStatus === 'authenticated') {
      try {
        console.log(`[LoginClient] Validating server auth session... (attempt ${attempt + 1}/${maxServerValidationAttempts})`);
        const response = await fetch('/api/debug-auth');
        
        console.log('[LoginClient] Server response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('[LoginClient] Server auth response:', JSON.stringify(data, null, 2));
          const hasServerAuth = data?.auth?.hasValidSession === true;
          
          if (hasServerAuth) {
            // Server confirms auth - proceed with redirect
            console.log('[LoginClient] Server confirms authentication, redirecting');
            const hasProfile = data?.profile?.exists === true;

            // Reset validation attempts counter
            setServerValidationAttempts(0);

            // Check for redirect URL in params (room invitation flow)
            const redirectUrl = searchParams.get('redirect');

            let destination;
            if (redirectUrl && redirectUrl.startsWith('/rooms/')) {
              // User was trying to join a room - redirect them there IMMEDIATELY
              // Skip onboarding even for new users when coming from room invite
              destination = redirectUrl;
              console.log('[LoginClient] Room invite flow - redirecting directly to room:', destination);
            } else {
              // Standard flow. Honor a safe ?next= (e.g. an invite return
              // trip) so every redirect source on /login agrees on the
              // destination instead of racing to different ones.
              destination = hasProfile
                ? getSafeRedirectPath(searchParams.get('next')) ?? '/dashboard'
                : '/onboarding';
              console.log('[LoginClient] Standard flow - redirecting to:', destination);
            }
            
            // Use window.location.replace for a stronger redirect
            window.location.replace(destination);
            return; // Exit early to prevent further processing
          } else {
            // Server disagrees with client - try again if under max attempts
            console.log('[LoginClient] Server reports not authenticated but client says authenticated');

            if (attempt < maxServerValidationAttempts - 1) {
              // Keep the state counter roughly in sync for debug display
              setServerValidationAttempts(attempt + 1);

              // Retry with exponential backoff
              const backoffDelay = 1000 * Math.pow(2, attempt);
              console.log(`[LoginClient] Retrying server validation in ${backoffDelay}ms (attempt ${attempt + 1})`);

              setTimeout(() => verifySeverSession(attempt + 1), backoffDelay);
            } else {
              // Max attempts reached, reset auth state
              console.log('[LoginClient] Max validation attempts reached, resetting auth state');
              resetAuthState();
            }
          }
        }
      } catch (error) {
        console.error('[LoginClient] Error validating auth with server:', error);
      }
    }
  };

  useEffect(() => {
    if (!mounted) return;
    
    // With Supabase native auth, we use the auth context's state instead of creating a new client
    const checkAuthState = async () => {
      if (typeof window === 'undefined') return;

      try {
        // Use the auth context's state directly
        // The auth context already manages the session and client
        if (session) {
          console.log('[LoginClient] Found existing Supabase session from auth context');
          
          // If we're on login page with a valid session, verify with server before redirecting
          if (isAuthenticated && !isLoading) {
            verifySeverSession();
          }
        } else {
          console.log('[LoginClient] No session found in auth context');
        }
      } catch (error) {
        console.error('[LoginClient] Error checking auth state:', error);
      }
    };

    // Check auth state
    checkAuthState();
    
    // Check for login_retry parameter to add validation delay
    const hasLoginRetry = searchParams.get('login_retry') === 'true';

    // Check for authentication errors in URL params or from prop
    const errorCode = error || searchParams.get('error');
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      setErrorMessage(ERROR_MESSAGES[errorCode]);
    } else if (error) {
      setErrorMessage(error);
    } else if (searchParams.get('auth_state_reset') === 'true') {
      setErrorMessage('Your authentication state was reset due to a session issue. Please sign in again.');
    } else if (searchParams.get('token_reset') === 'true') {
      setErrorMessage('Your authentication token was in an invalid format and has been reset. Please sign in again.');
    }
    
    // Show success message if provided
    if (message) {
      // Handle success message display if needed
    }
    
    // Use a timeout to prevent immediate redirects during page load
    // This gives a chance for auth state to stabilize
    let authCheckTimer: NodeJS.Timeout;
    
    if (isAuthenticated && !isLoading && authStatus === 'authenticated') {
      console.log('[LoginClient] User appears authenticated, scheduling server validation');

      // Add extra delay if we're in a retry situation
      const baseDelay = hasLoginRetry ? 2000 : 800;
      authCheckTimer = setTimeout(verifySeverSession, baseDelay);

      // If we have login_retry parameter, also capture how many times we've tried
      if (hasLoginRetry) {
        const retryCount = parseInt(searchParams.get('retry_count') || '0', 10);
        if (retryCount > 0) {
          setServerValidationAttempts(Math.min(retryCount, maxServerValidationAttempts - 1));
        }
      }
    }
    
    // Handle redirect for auth if needed (for branch deployments)
    if (redirectForAuth && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('-git-')) {
        // Extract branch name from hostname for Vercel preview URLs
        const branchMatch = hostname.match(/-git-([^-]+)-/);
        if (branchMatch && branchMatch[1]) {
          const branch = branchMatch[1];
          console.log(`[LoginClient] Detected branch deployment: ${branch}`);
          
          // For now, just log detection - we could implement branch-specific logic here
          // such as redirecting to the main domain for authentication if needed
        }
      }
    }
    
    // Clean up timer
    return () => {
      if (authCheckTimer) clearTimeout(authCheckTimer);
    };
  }, [isAuthenticated, isLoading, authStatus, router, searchParams, mounted, error, message, redirectForAuth]);
  
  // Choose which login form to use based on feature flags
  const LoginFormComponent = useImprovedForm ? LoginFormCompat : LoginForm;
  
  return (
    <>
      {showDebugInfo && mounted && (
        <div className={`text-xs px-2 py-1 mb-4 rounded text-center ${
          isUsingImprovedAuth 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' 
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
        }`}>
          Using {isUsingImprovedAuth ? 'improved' : 'legacy'} auth system
        </div>
      )}
      
      {mounted && errorMessage && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Only render the form after client-side hydration is complete */}
      {mounted ? (
        <LoginFormComponent initialSignUpMode={shouldShowSignUp} />
      ) : (
        <div className="h-[360px] w-full bg-card rounded-lg shadow-md"></div>
      )}
    </>
  );
}

interface LoginClientProps {
  redirectForAuth?: boolean;
  error?: string;
  message?: string;
  isSignUp?: boolean;
}

/**
 * LoginClient component that uses the compatibility auth hook
 * This works with either the legacy or improved auth system
 */
export default function LoginClientCompat({ redirectForAuth, error, message, isSignUp }: LoginClientProps) {
  // Check for feature flag to use the improved UI
  const useImprovedUI = useFeatureFlag(FeatureFlag.USE_IMPROVED_HEADER);
  
  // Choose colors based on theme
  const bgColor = useImprovedUI ? 'bg-background' : 'bg-gray-50';
  const headingColor = useImprovedUI ? 'text-foreground' : 'text-gray-900';
  const subTextColor = useImprovedUI ? 'text-muted-foreground' : 'text-gray-600';
  
  return (
    <div className={`min-h-screen flex items-center justify-center ${bgColor} py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className={`text-3xl font-extrabold ${headingColor}`}>
            Apprendo
          </h1>
          <p className={`mt-2 text-sm ${subTextColor}`}>
            Share your expertise through guided conversations
          </p>
        </div>
        
        <Suspense fallback={<div className="h-[400px] w-full bg-card rounded-lg shadow-md animate-pulse"></div>}>
          <LoginContent 
            redirectForAuth={redirectForAuth}
            error={error}
            message={message}
            isSignUp={isSignUp}
          />
        </Suspense>
        
        {/* Authentication troubleshooting link */}
        <div className="mt-4 text-center text-xs">
          <a 
            href="/api/auth/reset-session?redirectTo=/login" 
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            Having trouble signing in? Reset your session
          </a>
        </div>
        
        {/* Hidden AuthDebug component - only rendered on client side */}
        <ClientOnlyAuthDebug />
      </div>
    </div>
  );
}